import React, { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { CircularProgress, Tooltip, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { GridColDef, GridSortModel } from '@mui/x-data-grid'
import { useQuery } from '@tanstack/react-query'
import PaymentAPI from 'api/faber/PaymentAPI'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import { IFilterField } from 'common/AbstractDataGridToolbar/CustomDataGridFilter'
import { Toolbar } from 'common/AbstractDataGridToolbar/CustomToolbar'
import AbstractDataGridTooltip from 'common/AbstractDataGridTooltip'
import DateRangePicker from 'common/AbstractDateRangePicker/AbstractDateRangePicker'
import format from 'date-fns/format'
import { changeMonth } from 'helpers/date'
import OrbitSession from 'hooks/useCookies'
import { AllCustomerPayments } from 'types/api/faber/payment.type'

import FaberFinanceActionMenu from './FaberFinanceActionMenu'
export default function FaberFinanceTable() {
  const [planChecked, setPlanChecked] = useState<string[]>([])
  const [activationChecked, setActivationChecked] = useState<string[]>([])
  const [statusChecked, setStatusChecked] = useState<string[]>([])
  // set start date before faber launch
  const [startDate, setStartDate] = useState<Date>(new Date(2022, 9, 1))
  const [endDate, setEndDate] = useState<Date>(changeMonth(new Date(), +3))
  const [rows, setRows] = useState<AllCustomerPayments[]>([])
  const [filterActive, setFilterActive] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [tempSearchValue, setTempSearchValue] = useState<string>('')
  const [sortDate, setSortDate] = useState<string>('desc')
  const [searchValue, setSearchValue] = useState<string>('')
  const [searchCriteria, setSearchCriteria] = useState<string>('Email')

  const [pageOffset, setPageOffset] = useState(0)
  const [pageSize] = useState(10)
  const [page, setPage] = useState(0)
  const [rowCount, setRowCount] = useState(0)

  const role = OrbitSession.get('role')
  const faberToken = OrbitSession.get('faberToken')

  const searchOptions = ['Email', 'MSISDN']

  const onSearchValueChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    // CONVERT + TO %2B FOR QUERY URL
    setTempSearchValue(e.target.value.replace('+', '%2B'))
  }

  const onSearchSubmit = () => {
    setSearchValue(tempSearchValue)
    resetPage()
  }

  const handleSearchCriteriaChange = (e, v: string) => {
    setSearchCriteria(v)
  }

  useEffect(() => {
    resetPage()
  }, [startDate, endDate])

  const queryOptions = React.useMemo(
    () => ({
      pageOffset,
      pageSize,
      statusChecked,
      activationChecked,
      planChecked,
      searchCriteria,
      searchValue,
      filterStartDate: startDate,
      filterEndDate: endDate,
      sortDate
    }),
    [
      pageOffset,
      pageSize,
      statusChecked,
      planChecked,
      activationChecked,
      searchCriteria,
      searchValue,
      startDate,
      endDate,
      sortDate
    ]
  )

  const {
    data: getAllCustomerPayments,
    isLoading: gettingAllCustomerPayments
  } = useQuery(
    ['getAllCustomerPayments', [queryOptions]],
    async () => {
      const {
        pageOffset,
        pageSize,
        statusChecked,
        activationChecked,
        planChecked,
        searchCriteria,
        searchValue,
        sortDate,
        filterStartDate,
        filterEndDate
      } = queryOptions
      return PaymentAPI.getAllCustomerPayments(
        faberToken,
        pageSize,
        pageOffset,
        activationChecked,
        statusChecked,
        planChecked,
        searchCriteria,
        searchValue,
        sortDate,
        format(filterStartDate, 'yyyy-MM-dd'),
        format(filterEndDate, 'yyyy-MM-dd')
      )
    },
    {
      onError: () => {
        toast.error('Error getting payment details')
        setRows([])
        setIsLoading(false)
      }
    }
  )

  const handleSortModelChange = React.useCallback(
    (sortModel: GridSortModel) => {
      if (sortModel[0].sort === 'asc') {
        setSortDate('asc')
      } else {
        setSortDate('desc')
      }
    },
    []
  )

  /* PLACEHOLDER PENDING API */
  const updateRow = () => {
    return null
  }

  const handlePageChange = (newPage: number) => {
    const tmpOffset = newPage * pageSize
    setPage(newPage)
    setPageOffset(tmpOffset)
  }

  useEffect(() => {
    if (getAllCustomerPayments) {
      const { data: allSubscriptions, total } = getAllCustomerPayments.data.body
      if (allSubscriptions && allSubscriptions.length > 0) {
        setRows(allSubscriptions)
      }
      setIsLoading(false)
      setRowCount(total)
    } else {
      setRows([])
      setIsLoading(false)
    }
  }, [getAllCustomerPayments])

  function resetPage() {
    setPageOffset(0)
    setPage(0)
  }

  function handleMarkAsPaid() {
    toast.success('Updated payment status')
  }

  const columns: GridColDef[] = [
    {
      sortable: true,
      field: 'billStart',
      headerName: 'Billing Start Date',
      flex: 1,
      minWidth: 100,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: { row: AllCustomerPayments }) => {
        return (
          <Box sx={{ fontSize: '12px' }}>
            {format(new Date(params.row.billStart), 'dd MMM yyyy HH:mm')}
          </Box>
        )
      }
    },
    // HIDE PAYMENT TIME UNTIL API IS UPDATED
    {
      sortable: true,
      field: 'lastPaymentAt',
      headerName: 'Last Payment',
      flex: 1,
      minWidth: 100,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: { row: AllCustomerPayments }) => {
        return (
          <Box sx={{ fontSize: '12px' }}>
            {format(new Date(params.row.lastPaymentAt), 'dd MMM yyyy HH:mm')}
          </Box>
        )
      }
    },
    {
      sortable: false,
      field: 'orderID',
      headerName: 'Bill No',
      flex: 1.25,
      minWidth: 100,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: { row: AllCustomerPayments }) => {
        return (
          <AbstractDataGridTooltip>
            {params.row.orderID}
          </AbstractDataGridTooltip>
        )
      }
    },
    {
      sortable: false,
      field: 'customerName',
      headerName: 'Name',
      flex: 1.25,
      minWidth: 50,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: { row: AllCustomerPayments }) => {
        return (
          // TODO: refactor all tooltips to use this abstract component
          <AbstractDataGridTooltip>
            {params.row.customerName}
          </AbstractDataGridTooltip>
        )
      }
    },
    {
      sortable: false,
      field: 'MSISDN',
      headerName: 'MSISDN',
      flex: 1,
      minWidth: 100,
      filterable: false,
      disableColumnMenu: true
    },
    {
      sortable: false,
      field: 'customerID',
      headerName: 'Customer ID',
      flex: 1.5,
      minWidth: 100,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: { row: AllCustomerPayments }) => {
        return (
          <Tooltip title={params.row.customerID} placement='top'>
            <Typography sx={{ fontSize: '12px', color: '#253140' }}>
              {params.row.customerID}
            </Typography>
          </Tooltip>
        )
      }
    },
    {
      sortable: false,
      field: 'userEmail',
      headerName: 'Email',
      flex: 2,
      minWidth: 100,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: { row: AllCustomerPayments }) => {
        return (
          <Tooltip title={params.row.userEmail} placement='top'>
            <Typography sx={{ fontSize: '12px', color: '#253140' }}>
              {params.row.userEmail}
            </Typography>
          </Tooltip>
        )
      }
    },
    {
      sortable: false,
      field: 'paymentType',
      headerName: 'Payment Type',
      flex: 1,
      minWidth: 100,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: { row: AllCustomerPayments }) => {
        return (
          <Tooltip title={params.row.paymentType} placement='top'>
            <Typography sx={{ fontSize: '12px', color: '#253140' }}>
              {params.row.paymentType}
            </Typography>
          </Tooltip>
        )
      }
    },
    {
      sortable: false,
      field: 'planName',
      headerName: 'Plan',
      flex: 0.75,
      minWidth: 30,
      filterable: false,
      disableColumnMenu: true
    },
    {
      sortable: false,
      field: 'planStatus',
      headerName: 'Plan Status',
      flex: 0.5,
      minWidth: 75,
      filterable: false,
      disableColumnMenu: true
    },
    {
      sortable: false,
      field: 'paymentStatus',
      headerName: 'Payment Status',
      flex: 0.75,
      minWidth: 50,
      filterable: true,
      hideable: false
    },
    {
      sortable: false,
      field: 'actions',
      headerName: 'Actions',
      renderCell: (params: { row: AllCustomerPayments }) => {
        return role !== 'opsviewer' ? (
          <FaberFinanceActionMenu
            paymentRow={params.row}
            handleMarkAsPaid={handleMarkAsPaid}
            updateRow={updateRow}
          />
        ) : undefined
      },
      flex: 0.5,
      minWidth: 50,
      filterable: false,
      disableColumnMenu: true
    }
  ]

  const filterFields = useMemo(() => {
    let tempFilterFields: IFilterField[] = []
    tempFilterFields = [
      {
        values: ['Lite Pac', 'Pro Pac'],
        checked: planChecked,
        setChecked: setPlanChecked,
        name: 'Plan',
        index: 'planName'
      },
      {
        values: ['ACTIVE', 'CANCELLED', 'EXPIRING', 'SUSPENDED'],
        checked: activationChecked,
        setChecked: setActivationChecked,
        name: 'Plan Status',
        index: 'planStatus'
      },
      {
        values: ['SUCCESS', 'FAILED', 'PENDING', 'REFUNDED'],
        checked: statusChecked,
        setChecked: setStatusChecked,
        name: 'Payment Status',
        index: 'paymentStatus'
      }
    ]
    return tempFilterFields
  }, [planChecked, activationChecked, statusChecked])

  return !isLoading ? (
    <MUIDataGrid
      getRowId={(row: AllCustomerPayments) =>
        row.orderID.toString() +
        rows.indexOf(row).toString() +
        'row' +
        row.customerID
      }
      tableHeight='750px'
      columns={columns}
      rows={rows}
      pagination
      paginationMode='server'
      rowCount={rowCount}
      page={page}
      pageSize={pageSize}
      onPageChange={handlePageChange}
      loading={gettingAllCustomerPayments}
      disableColumnMenu={false}
      disableColumnSelector={true}
      sortingMode='server'
      sortingOrder={['desc', 'asc']}
      onSortModelChange={handleSortModelChange}
      components={{
        Toolbar: Toolbar
      }}
      componentsProps={{
        toolbar: {
          serverFilterProps: {
            filterFields,
            filterActive,
            setFilterActive
          },
          searchBar: 'server',
          serverSearchBarProps: {
            onSubmit: onSearchSubmit,
            onChange: onSearchValueChange,
            options: searchOptions,
            defaultValue: searchCriteria,
            handleChange: handleSearchCriteriaChange
          },
          dateRangePicker: (
            <DateRangePicker
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
            />
          ),
          filter: 'server'
        }
      }}
    />
  ) : (
    <CircularProgress />
  )
}
